import Cookies from "js-cookie";
import useModalsStore from "../store/modalsStore";
import i18n from "../i18n";
import { queryClient } from "..";
import useUserStore from "../store/userStore";

export const logout = () => {
    Cookies.remove("token");
    const setLoginModalOpen = useModalsStore.getState().setLoginModalOpen;
    setLoginModalOpen(true);
    window.location.href = "/";
};

export const getToken = () => {
    const token = Cookies.get("token");
    return token;
};

export const getBranchId = () => {
    const branchId = Cookies.get("branchId");
    return branchId;
};

export const getEmirateId = () => {
    const emirateId = Cookies.get("emirateId");
    return emirateId;
};

export const getRegionId = () => {
    const regionId = Cookies.get("regionId");
    return regionId;
};

export const getLanguage = () => {
    return i18n.language;
};

export const getCurrency = () => {
    const host =
        typeof window !== "undefined" ? window.location.hostname : "";
    if (host.startsWith("sa.") || getCountry() === "ksa") {
        return "SAR";
    }
    const currency = Cookies.get("currency");
    return currency;
};

export const getCountry = () => {
    const country = Cookies.get("country");
    return country;
};

export const getCountryPhoneCode = () =>
    ({ uae: "971", ksa: "966", om: "968" } as Record<string, string>)[
        getCountry() ?? ""
    ] ?? "971";

export const isSaudiSubdomain = (
    hostname: string = typeof window !== "undefined"
        ? window.location.hostname
        : "",
): boolean => {
    return hostname.startsWith("sa.");
};

export const getSaHostname = (
    hostname: string = typeof window !== "undefined"
        ? window.location.hostname
        : "",
): string => {
    if (hostname.startsWith("sa.")) {
        return hostname;
    }
    if (hostname === "127.0.0.1") {
        return "sa.localhost";
    }
    const prefixes = ["om.", "www."];
    for (const prefix of prefixes) {
        if (hostname.startsWith(prefix)) {
            return `sa.${hostname.slice(prefix.length)}`;
        }
    }
    return `sa.${hostname}`;
};

export const redirectToSaDomain = (): boolean => {
    if (typeof window === "undefined") return false;
    const currentHost = window.location.hostname;
    if (currentHost.startsWith("sa.")) {
        return false;
    }
    const targetHost = getSaHostname(currentHost);
    if (targetHost === currentHost) {
        return false;
    }
    const port = window.location.port ? `:${window.location.port}` : "";
    const targetUrl = `${window.location.protocol}//${targetHost}${port}${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.href = targetUrl;
    return true;
};

export const redirectToMainDomain = (): boolean => {
    if (typeof window === "undefined") return false;
    const currentHost = window.location.hostname;
    if (currentHost.startsWith("sa.")) {
        const targetHost = currentHost.slice("sa.".length);
        const port = window.location.port ? `:${window.location.port}` : "";
        const targetUrl = `${window.location.protocol}//${targetHost}${port}${window.location.pathname}${window.location.search}${window.location.hash}`;
        window.location.href = targetUrl;
        return true;
    }
    return false;
};

// Same rule as the GTM snippet in public/index.html
const getGtmCountry = () => {
    const host = window.location.hostname;
    if (host.startsWith("sa.")) return "ksa";
    if (host.startsWith("om.")) return "om";
    return getCountry() || "uae";
};

// A GTM container can't be unloaded, so reload to load the one for the new country.
// Returns true if a reload was triggered.
export const reloadIfGtmCountryChanged = (): boolean => {
    if (typeof window === "undefined") return false;
    const host = window.location.hostname;
    const currentCountry = getCountry();

    // If we are redirecting between domains (e.g. sa. for KSA or main domain for UAE),
    // a full-page navigation to the target domain is already occurring.
    // Do NOT reload as calling window.location.reload() would cancel the domain redirect!
    if (currentCountry === "ksa" && !host.startsWith("sa.")) {
        return false;
    }
    if (currentCountry === "uae" && host.startsWith("sa.")) {
        return false;
    }

    if (
        window.__gtmCountry === undefined ||
        window.__gtmCountry === getGtmCountry()
    )
        return false;
    window.location.reload();
    return true;
};

export const getLat = () => {
    const lat = Cookies.get("lat");
    return lat;
};

export const getLng = () => {
    const lng = Cookies.get("lng");
    return lng;
};

export const getSession = () => {
    const session = localStorage.getItem("session");
    if (!session) return null;
    const parsedSession = JSON.parse(session);
    const setUser = useUserStore.getState().setUser;
    const setMainAddress = useUserStore.getState().setMainAddress;

    setUser(parsedSession.user);
    setMainAddress(parsedSession.mainAddress);
};

export const getDefaultAddressId = () => {
    const defaultAddressId = Cookies.get("defaultAddressId");
    return defaultAddressId;
};

export const setToken = (token: string) => {
    Cookies.set("token", token);
};

export const setBranchId = (branchId: string) => {
    Cookies.set("branchId", branchId);
};

export const setEmirateId = (emirateId: string) => {
    Cookies.set("emirateId", emirateId);
};

export const setRegionId = (regionId: string) => {
    Cookies.set("regionId", regionId);
};

export const setLanguage = (language: string) => {
    Cookies.set("language", language);
};

export const setCurrency = (currency: string) => {
    Cookies.set("currency", currency);
};

export const setCountry = (country: string): boolean => {
    Cookies.set("country", country);
    if (country === "ksa") {
        Cookies.set("currency", "SAR");
        return redirectToSaDomain();
    } else if (country === "uae") {
        Cookies.set("currency", "AED");
        return redirectToMainDomain();
    } else if (country === "om") {
        Cookies.set("currency", "OMR");
    }
    return false;
};

export const setLat = (lat: string) => {
    Cookies.set("lat", lat);
};

export const setLng = (lng: string) => {
    Cookies.set("lng", lng);
};

export const invalidate = () => {
    queryClient.invalidateQueries({
        queryKey: ["global"],
    });
};

export const setSession = () => {
    const session = useUserStore.getState();
    localStorage.setItem("session", JSON.stringify(session));
};

export const setDefaultAddressId = (defaultAddressId: string) => {
    Cookies.set("defaultAddressId", defaultAddressId);
};

export const resetSession = () => {
    localStorage.removeItem("session");
    Cookies.remove("token");
    Cookies.remove("branchId");
    Cookies.remove("emirateId");
    Cookies.remove("regionId");
    Cookies.remove("language");
    Cookies.remove("currency");
    Cookies.remove("country");
    Cookies.remove("lat");
    Cookies.remove("lng");
};
